import { useLanguage } from "@/context/LanguageContext";
import InquiryForm from "@/components/InquiryForm";
import { Link } from "react-router-dom";

const SERVICES = [
  {
    key: "housewatch",
    img: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=2000&q=85",
    numeral: "I",
    highlight: {
      es: "Vigilancia silenciosa mientras usted no está.",
      en: "Quiet stewardship while you are away.",
      de: "Stille Fürsorge in Ihrer Abwesenheit.",
      ru: "Тихая забота, пока вас нет.",
    },
  },
  {
    key: "yacht",
    img: "https://images.pexels.com/photos/5074833/pexels-photo-5074833.jpeg?auto=compress&cs=tinysrgb&w=2000",
    numeral: "II",
    highlight: {
      es: "Salga al mar en el barco correcto.",
      en: "Step aboard the right vessel.",
      de: "An Bord des richtigen Schiffes.",
      ru: "На борту нужной яхты.",
    },
  },
  {
    key: "excursions",
    img: "https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?auto=format&fit=crop&w=2000&q=85",
    numeral: "III",
    highlight: {
      es: "Rutas privadas que nadie más recorre.",
      en: "Private routes no one else knows.",
      de: "Private Routen, die niemand sonst kennt.",
      ru: "Приватные маршруты, которые не найти в путеводителях.",
    },
  },
  {
    key: "jets",
    img: "https://images.unsplash.com/photo-1583500178690-f7dc9e3d7f9d?auto=format&fit=crop&w=2000&q=85",
    numeral: "IV",
    highlight: {
      es: "En el aire en pocas horas, no días.",
      en: "In the air within hours, not days.",
      de: "In der Luft binnen Stunden, nicht Tagen.",
      ru: "В воздухе за часы, а не дни.",
    },
  },
  {
    key: "cars",
    img: "https://images.pexels.com/photos/11960306/pexels-photo-11960306.jpeg?auto=compress&cs=tinysrgb&w=2000",
    numeral: "V",
    highlight: {
      es: "Su vehículo, entregado en la villa.",
      en: "Your car, delivered to the villa.",
      de: "Ihr Fahrzeug, an die Villa geliefert.",
      ru: "Ваш автомобиль — с доставкой к вилле.",
    },
  },
  {
    key: "mortgage",
    img: "https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=2000&q=85",
    numeral: "VI",
    highlight: {
      es: "Financiación privada, sin fricción.",
      en: "Private financing, without friction.",
      de: "Private Finanzierung, ohne Reibung.",
      ru: "Приватное финансирование — без трения.",
    },
  },
];

export default function Services() {
  const { t, lang } = useLanguage();

  return (
    <div>
      {/* CINEMATIC INTRO */}
      <section className="relative min-h-[85vh] flex items-end pb-16 md:pb-24 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=2400&q=80)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(15,16,18,0.7) 0%, rgba(15,16,18,0.5) 50%, rgba(247,245,240,0.85) 100%)",
          }}
        />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full hero-shadow-strong">
          <div className="overline text-white mb-4" style={{color:'#fff', textShadow: '0 1px 8px rgba(0,0,0,.6)'}}>{t("services.label")}</div>
          <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tighter font-light text-white max-w-4xl">
            {t("services_page.hero_a")} <span className="italic">{t("services_page.hero_italic")}</span>
            <br />
            {t("services_page.hero_b")}
          </h1>
          <p className="mt-8 text-lg md:text-xl max-w-2xl text-white leading-relaxed font-normal">
            {t("services_page.subhead")}
          </p>
        </div>
      </section>

      {/* EDITORIAL PARAGRAPH */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline mb-3">{t("services_page.note_label")}</div>
            <div className="hairline max-w-16" />
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="font-serif text-2xl md:text-3xl leading-[1.35] text-ink font-light">
              {t("services_page.note")}
            </p>
            <div className="mt-8 font-mono text-xs tracking-widest2 text-muted uppercase">
              {t("services_page.signature")}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES — one per row, editorial */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {SERVICES.map((s, i) => (
          <ServiceRow
            key={s.key}
            s={s}
            index={i}
            reverse={i % 2 === 1}
            t={t}
            lang={lang}
          />
        ))}
      </div>

      {/* NUMBERS */}
      <section className="py-24 md:py-32 bg-obsidian text-obsidian-text relative overflow-hidden mt-16">
        <div
          aria-hidden
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1776761420375-c50a5a3b2e0d?auto=format&fit=crop&w=2400&q=80)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(4px) brightness(0.5)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="overline text-obsidian-accent mb-3">{t("services_page.numbers_label")}</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight font-light max-w-3xl">
            {t("services_page.numbers_title")}
          </h2>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-10">
            {[
              ["47 min", t("services_page.stat_1")],
              ["24 / 7", t("services_page.stat_2")],
              ["82", t("services_page.stat_3")],
              ["100%", t("services_page.stat_4")],
            ].map(([value, label]) => (
              <div key={label}>
                <div className="font-serif text-5xl md:text-6xl font-light">{value}</div>
                <div className="overline mt-3 text-obsidian-muted">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING QUOTE */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="overline mb-6">{t("services_page.quote_by")}</div>
          <blockquote className="font-serif text-3xl md:text-5xl leading-[1.15] text-ink font-light italic">
            {t("services_page.quote")}
          </blockquote>
        </div>
      </section>

      {/* CTA */}
      <section
        id="service-inquiry"
        className="py-24 border-t border-line"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline mb-3">{t("contact.label")}</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight text-ink font-light">
              {t("contact.title")}
            </h2>
            <p className="mt-6 text-muted leading-relaxed max-w-md">
              {t("services_page.cta_body")}
            </p>
            <Link
              to="/contact"
              data-testid="services-full-contact"
              className="inline-block mt-8 text-sm underline underline-offset-8 decoration-1 hover:text-accent transition-colors"
            >
              {t("services_page.cta_full_contact")} →
            </Link>
          </div>
          <div className="md:col-span-7">
            <InquiryForm testIdPrefix="services-inquiry" service="concierge" />
          </div>
        </div>
      </section>
    </div>
  );
}

function ServiceRow({ s, index, reverse, t, lang }) {
  const highlight = s.highlight[lang] || s.highlight.en;
  return (
    <section
      id={s.key}
      data-testid={`service-section-${s.key}`}
      className={`py-24 md:py-32 border-t border-line grid md:grid-cols-12 gap-10 md:gap-16 items-center ${
        reverse ? "md:[direction:rtl]" : ""
      }`}
    >
      <div className="md:col-span-7 [direction:ltr]">
        <div className={`relative aspect-[4/3] md:aspect-[16/11] overflow-hidden group ${
          s.key === "cars" ? "bg-obsidian" : ""
        }`}>
          <img
            src={s.img}
            alt=""
            loading="lazy"
            className={`card-img absolute inset-0 w-full h-full ${
              s.key === "cars"
                ? "object-contain object-center p-6 md:p-10"
                : "object-cover"
            }`}
          />
          <div className={`absolute top-6 left-6 font-serif text-4xl italic opacity-90 ${
            s.key === "cars" ? "text-white/80" : "text-white"
          }`}>
            {s.numeral}
          </div>
        </div>
      </div>
      <div className="md:col-span-5 [direction:ltr]">
        <div className="overline mb-4">
          {t("services_page.chapter")} {s.numeral} — {t(`services.${s.key}.title`)}
        </div>
        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink font-light">
          {highlight}
        </h2>
        <div className="hairline my-8 max-w-24" />
        <p className="text-lg text-ink leading-relaxed font-normal">
          {t(`services.${s.key}.desc`)}
        </p>
        <a
          href="#service-inquiry"
          data-testid={`service-cta-${s.key}`}
          className="inline-block mt-10 text-xs uppercase tracking-widest2 underline underline-offset-8 decoration-1 hover:text-accent transition-colors"
        >
          {t("services.request")} →
        </a>
      </div>
    </section>
  );
}
